import { useState, useEffect, useRef } from 'react';
import { Calculator, Clock, Settings, TrendingUp, Layers, FileText } from 'lucide-react';
import TileCalculatorForm from '../../components/calculator/TileCalculatorForm';
import MultiSizeTileCalculatorForm from '../../components/calculator/MultiSizeTileCalculatorForm';
import CalculationResults from '../../components/calculator/CalculationResults';
import CalculationHistory from '../../components/calculator/CalculationHistory';
import CalculatorConfigModal from '../../components/calculator/CalculatorConfigModal';
import ViewCalculationModal from '../../components/calculator/ViewCalculationModal';
import ViewQuotationModal from '../../components/calculator/ViewQuotationModal';
import ConvertToSaleModal from '../../components/calculator/ConvertToSaleModal';
import QuotationBuilder from '../../components/calculator/QuotationBuilder';
import { getCalculatorStatistics } from '../../api/calculator';
import { toast } from 'react-toastify';

const TileCalculatorPage = () => {
  const [activeTab, setActiveTab] = useState('calculator');
  const [calculatorMode, setCalculatorMode] = useState('multi'); // 'multi' or 'legacy'
  const [calculationResult, setCalculationResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedProductForConfig, setSelectedProductForConfig] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [refreshHistory, setRefreshHistory] = useState(0);
  
  // Quotation modal states
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [showConvertQuotationModal, setShowConvertQuotationModal] = useState(false);
  const [currentQuotation, setCurrentQuotation] = useState(null);
  
  // Reference to quotation builder for accessing its state
  const quotationBuilderRef = useRef(null);

  // Get user role from localStorage
  const getUserRole = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.role || 'staff';
    } catch {
      return 'staff';
    }
  };

  const isAdmin = ['admin', 'super_admin'].includes(getUserRole());

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const response = await getCalculatorStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleCalculationComplete = (result) => {
    setCalculationResult(result);
    setShowResultModal(true);
    setRefreshHistory(prev => prev + 1);
    loadStatistics();
  };

  const handleNewCalculation = () => {
    setCalculationResult(null);
    setShowResultModal(false);
  };

  const handleConversionComplete = () => {
    setRefreshHistory(prev => prev + 1);
    loadStatistics();
    setCalculationResult(null);
    setShowResultModal(false);
  };

  const handleConfigureProduct = (product = null) => {
    setSelectedProductForConfig(product);
    setShowConfigModal(true);
  };

  const handleAddToQuotation = (calculation) => {
    // This is handled by the QuotationBuilder's internal state
    console.log('Added to quotation:', calculation);
  };

  // Handle viewing the current quotation
  const handleViewQuotation = (quotation) => {
    if (!quotation || !quotation.calculations || quotation.calculations.length === 0) {
      toast.warning('No calculations in the quotation yet');
      return;
    }
    setCurrentQuotation(quotation);
    setShowQuotationModal(true);
  };

  // Handle converting quotation to sale
  const handleConvertQuotation = (quotation) => {
    if (!quotation || !quotation.calculations || quotation.calculations.length === 0) {
      toast.warning('No calculations in the quotation to convert');
      return;
    }
    
    // Combine all calculations into a single calculation object for the ConvertToSaleModal
    const combinedCalculation = {
      calculation_number: `Quotation (${quotation.calculations.length} items)`,
      total_area_sqm: quotation.calculations.reduce((sum, c) => sum + (parseFloat(c.total_area_sqm) || 0), 0),
      results: quotation.calculations.flatMap(c => c.results || []),
      calculations: quotation.calculations
    };
    
    setCurrentQuotation(combinedCalculation);
    setShowConvertQuotationModal(true);
  };

  // Handle successful conversion
  const handleQuotationConversionComplete = (saleData) => {
    setShowConvertQuotationModal(false);
    setCurrentQuotation(null);
    setRefreshHistory(prev => prev + 1);
    loadStatistics();
    
    // Clear the quotation from localStorage
    localStorage.removeItem('current_quotation');
    
    toast.success('Quotation converted to sale successfully!');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      {/* Header */}
      <div className="bg-white shadow mb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calculator className="w-8 h-8 text-accent-gold flex-shrink-0" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-primary-black">Tile Calculator</h1>
                <p className="text-sm text-primary-slate">Calculate tiles needed for any area with cost estimation</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Calculator Mode Toggle */}
              <div className="flex bg-secondary-offWhite rounded-lg p-0.5 mr-2">
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    calculatorMode === 'multi' 
                      ? 'bg-white text-primary-black shadow-sm' 
                      : 'text-primary-slate'
                  }`}
                  onClick={() => setCalculatorMode('multi')}
                >
                  <Layers className="w-3 h-3 inline mr-1" />
                  Multi-Size
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    calculatorMode === 'legacy' 
                      ? 'bg-white text-primary-black shadow-sm' 
                      : 'text-primary-slate'
                  }`}
                  onClick={() => setCalculatorMode('legacy')}
                >
                  Product
                </button>
              </div>
              {isAdmin && (
                <button 
                  className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-goldDark transition-colors"
                  onClick={() => handleConfigureProduct()}
                >
                  <Settings className="w-5 h-5" />
                  Configure Products
                </button>
              )}
            </div>
          </div>

          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
              <div className="bg-secondary-offWhite rounded-lg p-4 border border-secondary-lightGray">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-gold/10 rounded-lg flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-accent-gold" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl lg:text-3xl font-bold text-primary-black">{statistics.total_calculations}</p>
                    <p className="text-xs text-primary-slate">Total Calculations</p>
                  </div>
                </div>
              </div>
              <div className="bg-secondary-offWhite rounded-lg p-4 border border-secondary-lightGray">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl lg:text-3xl font-bold text-primary-black">{statistics.converted_calculations}</p>
                    <p className="text-xs text-primary-slate">Converted to Sales</p>
                  </div>
                </div>
              </div>
              <div className="bg-secondary-offWhite rounded-lg p-4 border border-secondary-lightGray">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl lg:text-3xl font-bold text-primary-black">{statistics.conversion_rate}%</p>
                    <p className="text-xs text-primary-slate">Conversion Rate</p>
                  </div>
                </div>
              </div>
              <div className="bg-secondary-offWhite rounded-lg p-4 border border-secondary-lightGray">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl lg:text-3xl font-bold text-primary-black">{formatCurrency(statistics.converted_value)}</p>
                    <p className="text-xs text-primary-slate">Converted Value</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-2 mb-4 bg-white p-1 rounded-lg shadow-sm w-fit">
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'calculator'
                ? 'bg-accent-gold text-white'
                : 'text-primary-slate hover:bg-secondary-lightGray'
            }`}
            onClick={() => setActiveTab('calculator')}
          >
            <Calculator className="w-4 h-4" />
            Calculator
          </button>
          <button
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-accent-gold text-white'
                : 'text-primary-slate hover:bg-secondary-lightGray'
            }`}
            onClick={() => setActiveTab('history')}
          >
            <Clock className="w-4 h-4" />
            History
          </button>
        </div>

        {/* Main Content */}
        <div className="pb-8">
          {activeTab === 'calculator' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Calculator Form */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-4 md:p-6">
                {calculatorMode === 'multi' ? (
                  <MultiSizeTileCalculatorForm 
                    onCalculationComplete={handleCalculationComplete}
                    onConfigure={isAdmin ? handleConfigureProduct : null}
                  />
                ) : (
                  <>
                    {calculationResult ? (
                      <CalculationResults 
                        result={calculationResult}
                        onNewCalculation={handleNewCalculation}
                        onConversionComplete={handleConversionComplete}
                      />
                    ) : (
                      <TileCalculatorForm 
                        onCalculationComplete={handleCalculationComplete}
                        onConfigureProduct={isAdmin ? handleConfigureProduct : null}
                      />
                    )}
                  </>
                )}
              </div>
              
              {/* Quotation Builder Sidebar */}
              <div className="space-y-4">
                <QuotationBuilder 
                  currentCalculation={calculationResult}
                  onAddCalculation={handleAddToQuotation}
                  onClear={() => {}}
                  onViewQuotation={handleViewQuotation}
                  onConvertToSale={handleConvertQuotation}
                />
                
                {/* Quick Tips */}
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h4 className="text-sm font-semibold text-primary-black mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-accent-gold" />
                    Quick Tips
                  </h4>
                  <ul className="text-xs text-primary-slate space-y-2">
                    <li>• Use <strong>Multi-Size</strong> mode for floors with design accents</li>
                    <li>• Add multiple tile sizes with percentage allocation</li>
                    <li>• Build quotations by adding multiple calculations</li>
                    <li>• Share via WhatsApp, Email, or Print</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <CalculationHistory 
                key={refreshHistory}
                onConversionComplete={handleConversionComplete}
              />
            </div>
          )}
        </div>
      </div>

      {/* View Calculation Modal */}
      {showResultModal && calculationResult && (
        <ViewCalculationModal
          result={calculationResult}
          isOpen={showResultModal}
          onClose={() => setShowResultModal(false)}
          onAddToQuotation={handleAddToQuotation}
          onConversionComplete={handleConversionComplete}
        />
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <CalculatorConfigModal
          product={selectedProductForConfig}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedProductForConfig(null);
          }}
          onSave={() => {
            setShowConfigModal(false);
            setSelectedProductForConfig(null);
          }}
        />
      )}

      {/* View Quotation Modal */}
      {showQuotationModal && currentQuotation && (
        <ViewQuotationModal
          quotation={currentQuotation}
          onClose={() => {
            setShowQuotationModal(false);
            setCurrentQuotation(null);
          }}
        />
      )}

      {/* Convert Quotation to Sale Modal */}
      {showConvertQuotationModal && currentQuotation && (
        <ConvertToSaleModal
          calculation={currentQuotation}
          onClose={() => {
            setShowConvertQuotationModal(false);
            setCurrentQuotation(null);
          }}
          onSuccess={handleQuotationConversionComplete}
        />
      )}
    </div>
  );
};

export default TileCalculatorPage;

